import type { AnchorHTMLAttributes, ButtonHTMLAttributes, ReactNode } from 'react';
import { Loader2 } from 'lucide-react';
import { cn } from '@/lib/cn';

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'outline' | 'dangerOutline';
type Size = 'sm' | 'md';

const variants: Record<Variant, string> = {
  primary: 'bg-secondary text-white hover:bg-secondary-hover',
  secondary: 'bg-primary text-white hover:bg-primary-soft',
  outline: 'border border-line bg-surface text-ink hover:bg-canvas-soft',
  ghost: 'text-ink-muted hover:bg-canvas-soft hover:text-ink',
  danger: 'bg-danger text-white hover:brightness-95',
  // A destructive action still belongs to the row it sits in: same border and
  // height as `outline`, only the colour says what it does.
  dangerOutline: 'border border-danger/40 bg-surface text-danger hover:bg-danger-soft',
};

const sizes: Record<Size, string> = {
  sm: 'h-8 px-3 text-xs gap-1.5',
  md: 'h-10 px-4 text-sm gap-2',
};

interface Props extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  loading?: boolean;
  icon?: ReactNode;
}

const buttonClass = (variant: Variant, size: Size, className?: string) => cn(
  'inline-flex items-center justify-center rounded-[var(--radius-control)] font-medium',
  'transition-colors disabled:cursor-not-allowed disabled:opacity-50',
  variants[variant], sizes[size], className,
);

export function Button({
  variant = 'primary', size = 'md', loading, icon, className, children, disabled, ...rest
}: Props) {
  return (
    <button
      {...rest}
      disabled={disabled || loading}
      className={buttonClass(variant, size, className)}
    >
      {loading ? <Loader2 className="size-4 animate-spin" aria-hidden /> : icon}
      {children}
    </button>
  );
}

export function ButtonLink({
  variant = 'primary', size = 'md', icon, className, children, ...rest
}: AnchorHTMLAttributes<HTMLAnchorElement> & {
  variant?: Variant; size?: Size; icon?: ReactNode;
}) {
  return (
    <a {...rest} className={buttonClass(variant, size, className)}>
      {icon}
      {children}
    </a>
  );
}

/**
 * A square, icon-only button - the shape the topbar and sidebar need. It is
 * here rather than hand-rolled in each shell file so the hover and focus
 * treatment stays the same everywhere.
 */
export function IconButton({
  variant = 'ghost', className, children, ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: Variant }) {
  return (
    <button
      {...rest}
      className={cn(
        'relative inline-flex size-9 items-center justify-center rounded-[var(--radius-control)]',
        'transition-colors disabled:cursor-not-allowed disabled:opacity-50',
        variant === 'ghost' ? 'text-ink-muted hover:bg-canvas-soft hover:text-ink' : variants[variant],
        className,
      )}
    >
      {children}
    </button>
  );
}
